import type { UserAuthorizationType } from '@next-space/fe-api-idl';
import { CaptchaSendType } from '@next-space/fe-api-idl';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { useCheckComplete } from '../../hook/use-check-complete';
import { useInitYidunCaptcha } from '../../hook/use-init-yidun-captcha';
import { useLogin } from '../../hook/use-login';
import type { CombineViewProps } from '../../types';
import { AccountStatus, ViewType } from '../../types';
import { Avatar } from '../../uikit/web/avatar';
import { Button } from '../../uikit/web/button';
import { CaptchaComp } from '../../uikit/web/captcha';
import { Input } from '../../uikit/web/input';
import { Title } from '../../uikit/web/title';
import { loginInjector } from '../../utils/init';
import { INVITE_CODE_REQUIRED_TEXT, INVITE_CODE_TEXT } from '../../const';

/**绑定手机号 */
export const BindPhone: FC<CombineViewProps> = (props) => {
  const {
    accountStatus,
    account,
    accountError,
    setAccount,
    verifyCode,
    inviteCode,
    setInviteCode,
    verifyCodeError,
    setVerifyCode,
    sendVerifyCode,
    next,
    setAccountStatus,
    isPhoneNumber,
    setPromotionChannel,
    setYidunCaptcha,
    yidunCaptcha,
  } = useLogin({
    newInviteCode: props.inviteCode,
    password: props.password,
    authorization: props.authorization,
  });
  const allowClick = account.length > 0 && verifyCode.length > 0;
  const alreadyHasInviteCode = Boolean(props.inviteCode);
  const bindFail = accountStatus === AccountStatus.BIND_FAIL;
  const checkComplete = useCheckComplete(props.switchViewType);
  const [hideInviteCode, setHideInviteCode] = useState(true);
  const { initNECaptcha } = useInitYidunCaptcha(setYidunCaptcha);

  useEffect(() => {
    setAccountStatus(AccountStatus.NEED_BIND_PHONE);
  }, [setAccountStatus]);

  useEffect(() => {
    if (account) {
      setHideInviteCode(true);
    }
  }, [account]);

  useEffect(() => {
    if (bindFail) return;
    initNECaptcha();
  }, [initNECaptcha, bindFail]);

  useEffect(() => {
    setPromotionChannel(props.promotionChannel);
  }, [props.promotionChannel, setPromotionChannel]);

  const tryNext = async () => {
    const user = await next();
    if (!user) return;
    props.onRegister?.(user);
    if (!props.complete || checkComplete(user)) {
      props.onSuccess?.(user);
    }
  };

  if (!props.authorization) return null;
  if (bindFail) {
    return (
      <BindFail
        account={account}
        goBindPhone={() => {
          setAccount('');
          setVerifyCode('');
          setAccountStatus(AccountStatus.NEED_BIND_PHONE);
        }}
        goLogin={() => {
          props.switchViewType({ type: ViewType.LOGIN });
        }}
      />
    );
  }

  return (
    <div>
      <Title text="绑定手机号" />
      <div className="sm:px-5">
        <div className="text-t1 mt-2 text-center text-grey3">
          应国家法律要求，使用FlowUs之前需要绑定手机号
        </div>
        <Input
          maxLength={11}
          type="number"
          warning={accountError}
          className="mt-10 h-[50px] w-full px-3 h-md:text-h4"
          inputClassName="px-0"
          autoFocus={true}
          onEnter={tryNext}
          value={account}
          placeholder="请输入手机号"
          onChange={(v) => {
            setAccount(v.toString());
          }}
        />
        <div id="yidun-captcha" className="mt-5" />
        <Input
          type="number"
          value={verifyCode}
          maxLength={6}
          warning={verifyCodeError}
          onEnter={tryNext}
          className="mt-5 h-[50px] w-full px-3 h-md:text-h4"
          placeholder="请输入验证码"
          onChange={(v) => setVerifyCode(v.toString())}
          addonAfter={
            <CaptchaComp
              disable={!yidunCaptcha}
              onSendVerifyCode={() => {
                const isPhone = isPhoneNumber();
                isPhone &&
                  void loginInjector.request.infra.phonePublicData.raw(account).then((res) => {
                    if (res.code === 200) {
                      setHideInviteCode(true);
                    } else {
                      setHideInviteCode(false);
                    }
                  });
                return sendVerifyCode(CaptchaSendType.SIGNUP);
              }}
            />
          }
        />
        {!hideInviteCode && (
          <Input
            readonly={alreadyHasInviteCode && !__PRIVATE__}
            value={inviteCode}
            maxLength={6}
            onEnter={tryNext}
            className="mt-5 h-[50px] w-full px-3 h-md:text-h4"
            placeholder={props.inviteCodeRequired ? INVITE_CODE_REQUIRED_TEXT : INVITE_CODE_TEXT}
            onChange={setInviteCode}
          />
        )}
        <Button
          disable={!allowClick}
          onClick={tryNext}
          colorType="primary"
          className={`text-t1-medium mt-7 h-[50px] w-full !opacity-100 h-md:text-h4 ${
            !allowClick && 'opacity-[0.3]'
          }`}
          size="auto"
        >
          继续
        </Button>
      </div>
    </div>
  );
};

interface ChangeBindPhoneProps {
  onSuccess?: () => void;
  authorizationCode: string;
  userId: string;
  type: UserAuthorizationType;
}
export const ChangeBindPhone: FC<ChangeBindPhoneProps> = (props) => {
  const { onSuccess, authorizationCode, userId, type } = props;
  const {
    account,
    accountError,
    setVerifyCodeError,
    setAccount,
    verifyCode,
    verifyCodeError,
    setVerifyCode,
    sendVerifyCode,
  } = useLogin();

  const bindNewPhone = () => {
    void loginInjector.request.infra.changeBindPhone
      .raw(userId, {
        phone: account,
        captcha: verifyCode,
        type,
        code: authorizationCode,
      })
      .then((res) => {
        if (res.code === 200) {
          onSuccess?.();
        } else {
          setVerifyCodeError(res.msg);
        }
      });
  };

  return (
    <div className="next-modal pt-10 pb-5 px-7 flex flex-col items-center min-w-[400px]">
      <div className="mb-5 text-t1-medium">换绑手机号</div>
      <Input
        autoFocus
        type="number"
        maxLength={11}
        warning={accountError}
        value={account}
        className={'w-full mb-5 px-3'}
        inputClassName="h-8"
        onChange={(v) => setAccount(v.toString())}
      />
      <Input
        type="number"
        className={'w-full mb-8 px-3'}
        inputClassName="h-8"
        maxLength={6}
        value={verifyCode}
        warning={verifyCodeError}
        onChange={(v) => setVerifyCode(v.toString())}
        addonAfter={
          <CaptchaComp
            showIcon={false}
            onSendVerifyCode={() => {
              return sendVerifyCode(CaptchaSendType.LOGIN);
            }}
          />
        }
      />
      <Button
        className="self-start px-5"
        colorType="active"
        disable={!account || !verifyCode}
        onClick={() => bindNewPhone()}
      >
        完成绑定
      </Button>
    </div>
  );
};

interface BindFailProps {
  account: string;
  goLogin: () => void;
  goBindPhone: () => void;
}
const BindFail: FC<BindFailProps> = (props) => {
  const [user, setUser] = useState<{
    nickname: string;
    avatar: string;
    createdAt: string;
    backgroundColor: string;
  }>();
  useEffect(() => {
    void loginInjector.request.infra.phonePublicData(props.account).then(setUser);
  }, [props.account]);
  return (
    <div className="w-full px-8">
      <Title text="😑 绑定失败" />
      <div className="text-t1 mt-2 text-center text-grey3">
        您的手机号 {props.account} 已被下方账号绑定
      </div>
      {user && (
        <div className="relative mt-[40px] flex w-full items-center rounded-lg border border-grey7 px-4 py-8 shadow-md">
          <Avatar
            className="h-[50px] w-[50px] rounded-full text-white"
            name={user.nickname}
            icon={{ type: 'upload', value: user.avatar }}
            color={user.backgroundColor}
          />
          <div className="ml-3 text-grey3">
            <div>{user.nickname}</div>
            {user.createdAt && <div>{dayjs(user.createdAt).format('YYYY年M月D日')} 完成注册</div>}
          </div>
          <div className="text-t2 absolute top-4 right-4 rounded border border-red py-1 px-2 text-red">
            当前绑定
          </div>
        </div>
      )}
      <div className="mt-5 flex justify-between">
        <Button
          onClick={props.goBindPhone}
          colorType="white"
          className="text-t1-medium mr-4 h-[50px] w-full"
          size="auto"
        >
          重新绑定手机
        </Button>
        <Button
          onClick={props.goLogin}
          colorType="primary"
          className="text-t1-medium h-[50px] w-full"
          size="auto"
        >
          切换账号登录
        </Button>
      </div>
    </div>
  );
};
